// WCAG contrast checker.
//
// Implementa WCAG 2.2 Success Criterion 1.4.3 Contrast (Minimum) + 1.4.6
// Contrast (Enhanced) calculando relative luminance y el ratio entre dos
// colores (RFC W3C: https://www.w3.org/TR/WCAG22/#dfn-contrast-ratio).
//
// Soporta hex (#RGB, #RRGGBB) y `rgb(r, g, b)`. Colores no-parseables
// devuelven null (el caller muestra "desconocido" y no bloquea).

export interface ContrastResult {
  ratio: number;
  passes_aa: boolean;       // >= 4.5:1 (texto normal)
  passes_aa_large: boolean; // >= 3:1   (texto >= 18pt)
  passes_aaa: boolean;      // >= 7:1   (texto normal enhanced)
}

function parseHex(color: string): [number, number, number] | null {
  const c = color.trim().replace(/^#/, '');
  if (c.length === 3) {
    const [r, g, b] = [c[0]!, c[1]!, c[2]!].map((x) => parseInt(x + x, 16));
    return Number.isFinite(r! + g! + b!) ? [r!, g!, b!] : null;
  }
  if (c.length === 6) {
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    return Number.isFinite(r + g + b) ? [r, g, b] : null;
  }
  return null;
}

function parseRgb(color: string): [number, number, number] | null {
  const m = color.trim().match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!m) return null;
  return [parseInt(m[1]!, 10), parseInt(m[2]!, 10), parseInt(m[3]!, 10)];
}

function parseColor(color: string): [number, number, number] | null {
  return parseHex(color) ?? parseRgb(color);
}

function toLinear(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb;
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Calcula el contrast ratio entre dos colores. Devuelve null si alguno no
 * pudo parsearse.
 */
export function contrastRatio(fg: string, bg: string): ContrastResult | null {
  const rgbFg = parseColor(fg);
  const rgbBg = parseColor(bg);
  if (!rgbFg || !rgbBg) return null;

  const lumFg = luminance(rgbFg);
  const lumBg = luminance(rgbBg);
  const lighter = Math.max(lumFg, lumBg);
  const darker = Math.min(lumFg, lumBg);
  const ratio = (lighter + 0.05) / (darker + 0.05);

  return {
    ratio: Math.round(ratio * 100) / 100,
    passes_aa: ratio >= 4.5,
    passes_aa_large: ratio >= 3,
    passes_aaa: ratio >= 7,
  };
}
